import { z } from "zod";
import { DECIMAL_MAX_MAGNITUDE, clean } from "../decimalClean.js";
import type { DecimalNumber } from "../decimal.js";

// ============================================================================
// Decimal (money / quantity / rate / percentage)
// ============================================================================

/**
 * A money amount or percentage field (quantities, rates and unit factors: {@link decimalQuantity}). Same wire shape as
 * `z.number()` (JSON Schema `{"type":"number"}`, so OpenAPI and MCP tool schemas don't change), but typed
 * {@link DecimalNumber}: a raw float result such as `a - b` cannot be assigned to it. Compute with `dec` from
 * helpers/decimal.ts.
 *
 * On request schemas it also refuses |x| ≥ DECIMAL_MAX_MAGNITUDE (minor units would no longer fit in 15 digits and be
 * rounded away silently — the 2-decimal limit, as a schema cannot know the currency; a service that does checks
 * maxExactMagnitude(its decimals)) and `.overwrite(clean)` strips float residue a client sent
 * (`0.30000000000000004` → `0.3`).
 * Collection and response schemas are never parsed, so there the brand is compile-time only.
 */
export const decimalNumber = z
  .number()
  // Cleaned before the limit, so it sees the stored value: 9,999,999,999,999.996 cleans to 1e13 and is refused.
  .overwrite(clean)
  .refine((x) => Math.abs(x) < DECIMAL_MAX_MAGNITUDE, {
    message: `Amount is too large to keep its minor units exact (must be below ${String(DECIMAL_MAX_MAGNITUDE)})`,
  })
  .brand<"Decimal">();

/**
 * A quantity, rate or unit-conversion factor: {@link decimalNumber} without its size limit, which keeps a money
 * amount's minor units exact. These are not held in minor units and are large where the unit is small (a km³ in mm³
 * is a factor of 1e18; a takeoff measured in mm³). Same brand, cleaning and wire shape.
 * Also a stored money total echoed back or parsed by the web (a rate analysis's total): built from quantities and
 * rates, it can pass the limit, which is for typed input.
 */
export const decimalQuantity = z.number().overwrite(clean).brand<"Decimal">();

// Every other number says what it is too (decimal-safety/explicit-number-kind refuses a bare `z.number()`), so the
// choice between exact money maths and a plain number is made — and visible in review — at every field. These three
// validate exactly like `z.number()`; the name is the declaration.

/** A count, counter, index, level, serial, version or byte size: a number that is never money. */
export const countNumber = z.number();

/** A point in time as milliseconds since the Unix epoch (`Date.now()`). */
export const timestampMs = z.number();

/**
 * Any other number that is never money, a quantity, a rate or a percentage: a coordinate, a size in pixels, a duration,
 * a zoom level, a score. A money / quantity / rate / % field is {@link decimalNumber} or {@link decimalQuantity}.
 * Also a value of any meaning that the platform never computes with: a user's form answer or custom field, vendor BIM
 * property data, an error-message parameter. Code that does maths on one parses it with `dec` first (the BIM readers,
 * the formula evaluator).
 */
export const nonMoneyNumber = z.number();

export type { DecimalNumber };
